import {
  rolesControllerAssignPermissions,
  rolesControllerDeleteRole,
  rolesControllerFindOne,
  rolesControllerRemovePermission,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Bell,
  BookOpen,
  Calendar,
  Check,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Copy,
  CreditCard,
  FileText,
  FlaskConical,
  Lock,
  MapPin,
  Pencil,
  Search,
  Settings,
  Shield,
  Trash2,
  User,
  Users,
  Zap,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AppLayout } from '../../../../components/layout/app-layout';
import { Breadcrumb, buildDetailItems } from '../../../../components/layout/breadcrumb';
import { GradientAvatar } from '../../../../components/shared/gradient-avatar';
import { Button } from '../../../../components/ui/button';
import { Skeleton } from '../../../../components/ui/skeleton';
import { Tabs } from '../../../../components/ui/tabs';
import { usePermissions } from '../../../../hooks/use-permissions';
import { cn } from '../../../../lib/utils';
import { useAuthStore } from '../../../../stores/auth.store';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AssignedUser {
  id: string;
  name: string;
  email: string;
  designation: string | null;
}

interface RolePermission {
  id: string;
  key: string;
  resource: string;
  action: string;
  field: string | null;
  description?: string | null;
  assigned: boolean;
}

interface RoleDetail {
  id: string;
  name: string;
  description?: string | null;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
  users: AssignedUser[];
  permissions: RolePermission[];
}

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/users/roles/$roleId/')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('role:view')) throw redirect({ to: '/dashboard' });
  },
  component: RoleDetailPage,
});

// ── Resource config ───────────────────────────────────────────────────────────

const RESOURCE_META: Record<
  string,
  {
    label: string;
    icon: React.ElementType;
    iconBg: string;
    iconColor: string;
  }
> = {
  customer: {
    label: 'Customers',
    icon: Users,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  permit: { label: 'Permits', icon: FileText, iconBg: 'bg-blue-100', iconColor: 'text-blue-600' },
  payment: { label: 'Payments', icon: CreditCard, iconBg: 'bg-sky-100', iconColor: 'text-sky-600' },
  notification: {
    label: 'Notifications',
    icon: Bell,
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
  },
  user: { label: 'Users', icon: User, iconBg: 'bg-violet-100', iconColor: 'text-violet-600' },
  role: { label: 'Roles', icon: Shield, iconBg: 'bg-indigo-100', iconColor: 'text-indigo-600' },
  config: {
    label: 'Configuration',
    icon: Settings,
    iconBg: 'bg-gray-100',
    iconColor: 'text-gray-600',
  },
  inspection: {
    label: 'Inspections',
    icon: ClipboardCheck,
    iconBg: 'bg-teal-100',
    iconColor: 'text-teal-600',
  },
  pledge: {
    label: 'Pledge Study',
    icon: BookOpen,
    iconBg: 'bg-rose-100',
    iconColor: 'text-rose-600',
  },
  survey: {
    label: 'Survey',
    icon: ClipboardList,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
  },
  'request-type': {
    label: 'Request Types',
    icon: Settings,
    iconBg: 'bg-slate-100',
    iconColor: 'text-slate-600',
  },
  'document-type': {
    label: 'Document Types',
    icon: FileText,
    iconBg: 'bg-cyan-100',
    iconColor: 'text-cyan-600',
  },
  'fee-component': {
    label: 'Fee Components',
    icon: CreditCard,
    iconBg: 'bg-yellow-100',
    iconColor: 'text-yellow-600',
  },
  'pledge-config': {
    label: 'Pledge Studies',
    icon: BookOpen,
    iconBg: 'bg-pink-100',
    iconColor: 'text-pink-600',
  },
  'sm-pledge-config': {
    label: 'SM Pledge Studies',
    icon: BookOpen,
    iconBg: 'bg-pink-100',
    iconColor: 'text-pink-600',
  },
  'payment-gateway': {
    label: 'Payment Gateway',
    icon: Zap,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
  },
  system: {
    label: 'Payment Gateway',
    icon: Zap,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
  },
  'sm-study': {
    label: 'SM Study Requests',
    icon: BookOpen,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
  },
  'sm-site-visit': {
    label: 'SM Site Visits',
    icon: MapPin,
    iconBg: 'bg-lime-100',
    iconColor: 'text-lime-600',
  },
  'sm-lab': {
    label: 'SM Lab Requests',
    icon: FlaskConical,
    iconBg: 'bg-teal-100',
    iconColor: 'text-teal-600',
  },
  'sm-study-report': {
    label: 'SM Study Reports',
    icon: FileText,
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-600',
  },
  'sm-extension': {
    label: 'SM Extensions',
    icon: Clock,
    iconBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
  },
};

const RESOURCE_TAB: Record<string, 'general' | 'kalba' | 'sharjah'> = {
  user: 'general',
  role: 'general',
  config: 'general',
  'request-type': 'general',
  'document-type': 'general',
  'fee-component': 'general',
  'pledge-config': 'kalba',
  'payment-gateway': 'general',
  system: 'general',
  notification: 'general',
  survey: 'general',
  customer: 'general',
  permit: 'kalba',
  inspection: 'kalba',
  pledge: 'kalba',
  'sm-study': 'sharjah',
  'sm-site-visit': 'sharjah',
  'sm-lab': 'sharjah',
  'sm-study-report': 'sharjah',
  'sm-extension': 'sharjah',
  'sm-pledge-config': 'sharjah',
};

function getResourceMeta(resource: string) {
  return (
    RESOURCE_META[resource] ?? {
      label: resource.charAt(0).toUpperCase() + resource.slice(1),
      icon: Settings,
      iconBg: 'bg-gray-100',
      iconColor: 'text-gray-600',
    }
  );
}

function derivePermissionName(action: string, resource: string): string {
  const { label } = getResourceMeta(resource);
  return `${action.charAt(0).toUpperCase() + action.slice(1)} ${label}`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ── Role hero avatar ──────────────────────────────────────────────────────────

function RoleHeroAvatar() {
  return (
    <div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-green-700 shadow-md">
      <Shield size={32} className="text-white" strokeWidth={1.5} />
    </div>
  );
}

// ── Custom checkbox ───────────────────────────────────────────────────────────

function PermCheckbox({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={cn(
        'h-5 w-5 shrink-0 rounded-[5px] border-2 flex items-center justify-center transition-all duration-150',
        checked
          ? 'bg-success-500 border-success-500'
          : 'bg-white border-gray-300 hover:border-gray-400',
        disabled && 'cursor-default',
      )}
    >
      {checked && <Check size={11} strokeWidth={3} className="text-white" />}
    </button>
  );
}

// ── Permission row ────────────────────────────────────────────────────────────

function PermissionRow({
  perm,
  canEdit,
  onToggle,
}: {
  perm: RolePermission;
  canEdit: boolean;
  onToggle: (id: string, assigned: boolean) => void;
}) {
  const name = derivePermissionName(perm.action, perm.resource);

  return (
    <div
      className={cn(
        'flex items-start gap-4 px-5 py-3.5 transition-colors',
        perm.assigned ? 'bg-success-50/50' : 'hover:bg-gray-50/70',
      )}
    >
      <div className="mt-0.5">
        <PermCheckbox
          checked={perm.assigned}
          disabled={!canEdit}
          onChange={(v) => onToggle(perm.id, v)}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2.5">
          <span
            className={cn(
              'inline-flex items-center rounded-md px-2 py-0.5 font-mono text-[11px] font-medium leading-tight',
              perm.assigned ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-500',
            )}
          >
            {perm.key}
          </span>
          <span
            className={cn(
              'text-sm font-semibold',
              perm.assigned ? 'text-gray-900' : 'text-gray-600',
            )}
          >
            {name}
          </span>
        </div>
        {perm.description && (
          <p className="mt-1 text-xs text-gray-400 leading-relaxed">{perm.description}</p>
        )}
      </div>
      {perm.assigned && (
        <span className="mt-0.5 shrink-0 inline-flex items-center rounded-full border border-success-200 bg-white px-3 py-1 text-xs font-semibold text-success-600">
          Enabled
        </span>
      )}
    </div>
  );
}

// ── Resource group ────────────────────────────────────────────────────────────

function ResourceGroup({
  resource,
  perms,
  expanded,
  onToggleExpand,
  canEdit,
  onToggle,
  onSelectAll,
}: {
  resource: string;
  perms: RolePermission[];
  expanded: boolean;
  onToggleExpand: () => void;
  canEdit: boolean;
  onToggle: (id: string, assigned: boolean) => void;
  onSelectAll: (resource: string, assign: boolean) => void;
}) {
  const meta = getResourceMeta(resource);
  const Icon = meta.icon;
  const assignedCount = perms.filter((p) => p.assigned).length;
  const total = perms.length;
  const pct = total > 0 ? Math.round((assignedCount / total) * 100) : 0;
  const allAssigned = assignedCount === total;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm">
      {/* Group header */}
      <div className="flex items-center gap-4 px-5 py-4">
        {/* Circle icon */}
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
            meta.iconBg,
          )}
        >
          <Icon size={18} className={meta.iconColor} />
        </div>

        {/* Name + progress */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-bold text-gray-900">{meta.label}</span>
            <span className="inline-flex items-center rounded-full bg-success-500 px-2 py-0.5 text-[11px] font-bold text-white">
              {assignedCount}/{total}
            </span>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="flex-1 h-2 overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full bg-success-500 transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-[11px] font-semibold text-gray-500 w-8 shrink-0">{pct}%</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          {canEdit && (
            <button
              type="button"
              onClick={() => onSelectAll(resource, !allAssigned)}
              className="rounded-full border border-gray-300 bg-white px-3.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              {allAssigned ? 'Deselect all' : 'Select all'}
            </button>
          )}
          <button
            type="button"
            onClick={onToggleExpand}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 transition-colors"
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Permissions list */}
      {expanded && (
        <div className="border-t border-gray-100 divide-y divide-gray-100">
          {perms.map((p) => (
            <PermissionRow key={p.id} perm={p} canEdit={canEdit} onToggle={onToggle} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function RoleDetailPage() {
  const { roleId } = Route.useParams();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const qc = useQueryClient();
  const { location } = useRouterState();

  const [search, setSearch] = useState('');
  const [debSearch, setDebSearch] = useState('');
  const [expandAll, setExpandAll] = useState<boolean | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebSearch(search), 200);
    return () => clearTimeout(timerRef.current);
  }, [search]);

  const {
    data: role,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['role', roleId],
    queryFn: () => rolesControllerFindOne({ id: roleId }) as unknown as Promise<RoleDetail>,
  });

  useEffect(() => {
    if (!role) return;
    const resources = [...new Set(role.permissions.map((p) => p.resource))];
    setExpanded(Object.fromEntries(resources.map((r) => [r, true])));
  }, [role?.id]);

  useEffect(() => {
    if (expandAll === null || !role) return;
    const resources = [...new Set(role.permissions.map((p) => p.resource))];
    setExpanded(Object.fromEntries(resources.map((r) => [r, expandAll])));
  }, [expandAll]);

  const assignMutation = useMutation({
    mutationFn: (ids: string[]) =>
      rolesControllerAssignPermissions({ id: roleId, requestBody: { permissionIds: ids } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['role', roleId] }),
  });

  const removeMutation = useMutation({
    mutationFn: (permId: string) =>
      rolesControllerRemovePermission({ id: roleId, permissionId: permId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['role', roleId] }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => rolesControllerDeleteRole({ id: roleId }),
    onSuccess: () => navigate({ to: '/users/roles' }),
  });

  const canEdit = can('role:assign-permissions') || can('user:view');
  const canDelete = !role?.isSystem && (can('role:create') || can('user:view'));

  const assignedCount = role?.permissions.filter((p) => p.assigned).length ?? 0;
  const totalCount = role?.permissions.length ?? 0;
  const users = role?.users ?? [];

  const byResource = useMemo(() => {
    const perms = role?.permissions ?? [];
    const filtered = debSearch
      ? perms.filter(
          (p) =>
            p.key.toLowerCase().includes(debSearch.toLowerCase()) ||
            (p.description ?? '').toLowerCase().includes(debSearch.toLowerCase()) ||
            derivePermissionName(p.action, p.resource)
              .toLowerCase()
              .includes(debSearch.toLowerCase()),
        )
      : perms;
    return filtered.reduce<Record<string, RolePermission[]>>((acc, p) => {
      if (!acc[p.resource]) acc[p.resource] = [];
      acc[p.resource].push(p);
      return acc;
    }, {});
  }, [role?.permissions, debSearch]);

  const [activeTab, setActiveTab] = useState<'general' | 'kalba' | 'sharjah'>('general');

  const resources = Object.keys(byResource).sort();
  const tabResources = resources.filter((r) => (RESOURCE_TAB[r] ?? 'general') === activeTab);
  const allExpanded = tabResources.length > 0 && tabResources.every((r) => expanded[r]);

  const tabCounts = useMemo(() => {
    const perms = role?.permissions ?? [];
    const tabs = ['general', 'kalba', 'sharjah'] as const;
    return Object.fromEntries(
      tabs.map((tab) => {
        const tabPerms = perms.filter((p) => (RESOURCE_TAB[p.resource] ?? 'general') === tab);
        return [
          tab,
          { assigned: tabPerms.filter((p) => p.assigned).length, total: tabPerms.length },
        ];
      }),
    ) as Record<'general' | 'kalba' | 'sharjah', { assigned: number; total: number }>;
  }, [role?.permissions]);

  function handleToggle(id: string, assign: boolean) {
    if (assign) assignMutation.mutate([id]);
    else removeMutation.mutate(id);
  }

  function handleSelectAll(resource: string, assign: boolean) {
    const perms = byResource[resource] ?? [];
    if (assign) {
      const ids = perms.filter((p) => !p.assigned).map((p) => p.id);
      if (ids.length) assignMutation.mutate(ids);
    } else {
      perms.filter((p) => p.assigned).forEach((p) => removeMutation.mutate(p.id));
    }
  }

  const breadcrumbItems = buildDetailItems(location.pathname, role?.name ?? '…', () =>
    navigate({ to: '/users/roles' }),
  );

  // ── Loading ───────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <AppLayout>
        <div className="space-y-5">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-44 w-full rounded-2xl" />
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2 space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-28 w-full rounded-2xl" />
              ))}
            </div>
            <div className="space-y-3">
              <Skeleton className="h-64 w-full rounded-2xl" />
              <Skeleton className="h-40 w-full rounded-2xl" />
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (isError || !role) {
    return (
      <AppLayout>
        <div className="py-20 text-center text-error-500">Role not found.</div>
      </AppLayout>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <AppLayout>
      <Breadcrumb items={breadcrumbItems} />

      {/* ── Header card ── */}
      <div className="mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {/* Gradient top stripe */}
        <div className="h-1 bg-gradient-to-r from-emerald-400 via-green-500 to-teal-400" />

        <div className="flex items-start justify-between gap-6 p-6">
          {/* Left: avatar + info */}
          <div className="flex items-start gap-5">
            <RoleHeroAvatar />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900 leading-tight">{role.name}</h1>
                {role.isSystem ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-500">
                    <Lock size={11} />
                    System Role
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
                    <Settings size={11} />
                    Custom Role
                  </span>
                )}
              </div>
              {role.description && (
                <p className="text-sm text-gray-500 mb-4 leading-relaxed max-w-xl">
                  {role.description}
                </p>
              )}
              {/* Meta strip */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-500">
                <span className="flex items-center gap-1.5">
                  <Zap size={13} className="text-success-500 shrink-0" />
                  Permissions:{' '}
                  <strong className="font-bold text-gray-900 ml-1">
                    {assignedCount} / {totalCount} enabled
                  </strong>
                </span>
                <span className="text-gray-200 hidden sm:block">|</span>
                <span className="flex items-center gap-1.5">
                  <Users size={13} className="text-gray-400 shrink-0" />
                  Users:{' '}
                  <strong className="font-bold text-gray-900 ml-1">{users.length} assigned</strong>
                </span>
                <span className="text-gray-200 hidden sm:block">|</span>
                <span className="flex items-center gap-1.5">
                  <Calendar size={13} className="text-gray-400 shrink-0" />
                  Created:{' '}
                  <strong className="font-bold text-gray-900 ml-1">
                    {fmtDate(role.createdAt)}
                  </strong>
                </span>
                <span className="text-gray-200 hidden sm:block">|</span>
                <span className="flex items-center gap-1.5">
                  <Clock size={13} className="text-gray-400 shrink-0" />
                  Updated:{' '}
                  <strong className="font-bold text-gray-900 ml-1">
                    {fmtDate(role.updatedAt)}
                  </strong>
                </span>
              </div>
            </div>
          </div>

          {/* Right: action buttons */}
          <div className="flex shrink-0 items-center gap-2">
            {(can('role:create') || can('user:view')) && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate({ to: '/users/roles/$roleId/edit', params: { roleId } })}
              >
                <Pencil size={14} />
                Edit Role
              </Button>
            )}
            <Button variant="outline" size="sm" disabled>
              <Copy size={14} />
              Duplicate
            </Button>
            {canDelete && (
              <button
                type="button"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                title="Delete role"
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 text-gray-400 hover:border-error-200 hover:bg-error-50 hover:text-error-500 transition-colors"
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Two-column body ── */}
      <div className="grid grid-cols-3 gap-6 items-start">
        {/* ── Left: permissions panel ── */}
        <div className="col-span-2 space-y-4">
          {/* Search + controls toolbar */}
          <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
            <div className="relative flex-1">
              <Search
                size={14}
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search permissions by name or slug..."
                className="h-9 w-full rounded-xl border border-gray-200 bg-gray-50 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-brand-100 focus:border-brand-400 transition-all placeholder:text-gray-400"
              />
            </div>
            <button
              type="button"
              onClick={() => setExpandAll(!allExpanded)}
              className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              {allExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              {allExpanded ? 'Collapse all' : 'Expand all'}
            </button>
            <div className="flex items-center gap-1.5 rounded-full border border-success-200 bg-success-50 px-3.5 py-1.5 whitespace-nowrap">
              <Zap size={12} className="text-success-500" />
              <span className="text-xs font-bold text-success-600">
                {assignedCount} of {totalCount} enabled
              </span>
            </div>
          </div>

          {/* Permission tab selector */}
          <Tabs
            tabs={[
              {
                id: 'general',
                label: 'General',
                badge: `${tabCounts.general.assigned}/${tabCounts.general.total}`,
              },
              {
                id: 'kalba',
                label: 'Kalba Municipality',
                badge: `${tabCounts.kalba.assigned}/${tabCounts.kalba.total}`,
              },
              {
                id: 'sharjah',
                label: 'Sharjah Municipality',
                badge: `${tabCounts.sharjah.assigned}/${tabCounts.sharjah.total}`,
              },
            ]}
            activeTab={activeTab}
            onTabChange={(id) => setActiveTab(id as 'general' | 'kalba' | 'sharjah')}
          />

          {/* Resource accordion groups */}
          <div className="space-y-3">
            {tabResources.length === 0 ? (
              <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-sm">
                <Shield size={36} className="mx-auto mb-3 text-gray-200" />
                <p className="text-sm font-semibold text-gray-500">No permissions found</p>
                {debSearch && (
                  <p className="mt-1 text-xs text-gray-400">Try a different search term</p>
                )}
              </div>
            ) : (
              tabResources.map((resource) => (
                <ResourceGroup
                  key={resource}
                  resource={resource}
                  perms={byResource[resource]}
                  expanded={expanded[resource] ?? true}
                  onToggleExpand={() =>
                    setExpanded((prev) => ({ ...prev, [resource]: !prev[resource] }))
                  }
                  canEdit={canEdit}
                  onToggle={handleToggle}
                  onSelectAll={handleSelectAll}
                />
              ))
            )}
          </div>
        </div>

        {/* ── Right sidebar ── */}
        <div className="space-y-4">
          {/* Assigned Users */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div>
                <h3 className="text-sm font-bold text-gray-900">Assigned Users</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {users.length} member{users.length !== 1 ? 's' : ''}
                </p>
              </div>
              {(can('user:view') || can('role:assign-permissions')) && (
                <Button variant="primary" size="sm" onClick={() => navigate({ to: '/users' })}>
                  <Users size={13} />
                  Manage
                </Button>
              )}
            </div>

            <div className="divide-y divide-gray-100">
              {users.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6">
                  No users assigned to this role.
                </p>
              ) : (
                users.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => navigate({ to: '/users/$userId', params: { userId: u.id } })}
                    className="flex w-full items-center gap-3 px-5 py-3.5 text-left hover:bg-gray-50 transition-colors"
                  >
                    <GradientAvatar name={u.name} size="md" shape="circle" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{u.name}</p>
                      <p className="text-xs text-gray-400 truncate mt-0.5">
                        {u.designation ?? u.email}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Role Details */}
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Role Details</h3>
            <div className="space-y-0">
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-sm text-gray-500">Type</span>
                {role.isSystem ? (
                  <span className="text-sm font-semibold text-gray-600">System</span>
                ) : (
                  <span className="text-sm font-semibold text-blue-600">Custom</span>
                )}
              </div>
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-sm text-gray-500">Created by</span>
                <span className="text-sm font-semibold text-gray-800">—</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-sm text-gray-500">Created on</span>
                <span className="text-sm font-semibold text-gray-800">
                  {fmtDate(role.createdAt)}
                </span>
              </div>
              <div className="flex items-center justify-between py-3">
                <span className="text-sm text-gray-500">Total permissions</span>
                <span className="text-sm font-semibold text-gray-800">{totalCount}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
